import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CSRF_HEADER, readCsrfToken } from './csrf';

/**
 * The API's **origin**, not its path — `/api` is appended below, so this must never contain it.
 *
 * Locally that means `http://localhost:8080`, because Vite and the backend are separate servers.
 * In production it is empty: Caddy serves the site and proxies the API on one origin, so requests
 * go to `/api/...` on whatever host the page was loaded from, and there is no CORS.
 *
 * Setting this to `/api` produces requests to `/api/api/...`, which the backend answers with 401
 * rather than 404 — the path misses the public security chain and lands in the authenticated one.
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * The single RTK Query service. Endpoints live with their features and are attached via
 * `injectEndpoints`, so adding a screen never means editing this file.
 *
 * Two things every request needs and neither is a default:
 *  - `credentials: 'include'`, because auth is a session cookie on a different origin;
 *  - the CSRF header on writes, because the backend's session chain enforces double-submit.
 */
export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_BASE_URL}/api`,
    credentials: 'include',
    prepareHeaders: (headers) => {
      const token = readCsrfToken();
      // Harmless on GETs, required on everything else — simpler than branching on method.
      if (token) headers.set(CSRF_HEADER, token);
      return headers;
    },
  }),
  tagTypes: [
    'Events', 'Event', 'Me', 'Subscriptions', 'AdminEvents',
    'OrganiserLinks', 'PlatformRequests', 'Analytics',
    'SavedEvents', 'Notifications', 'MyEvents', 'AdminUsers', 'Health', 'Discoveries',
  ],
  endpoints: () => ({}),
});
